"use client";

import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { createContext, useContext, useRef, useState, Children } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";

interface CarouselContextValue {
  activeIndex: number;
}

const CarouselContext = createContext<CarouselContextValue | null>(null);

const useCarouselContext = () => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error("Carousel subcomponents must be used within BlockSideBySideDemoCarousel");
  }
  return context;
};

interface SlideProps {
  children: React.ReactNode;
  index: number;
  carouselId: string;
}

const SideBySideCarouselSlide = ({ children, carouselId, index }: SlideProps) => {
  const { activeIndex } = useCarouselContext();
  const isActive = activeIndex === index;
  return (
    <div
      aria-labelledby={`${carouselId}-carousel-tab-${index}`}
      className='flex w-full'
      id={`${carouselId}-carousel-panel-${index}`}
      role='tabpanel'
      tabIndex={0}
      style={{
        position: isActive ? "relative" : "absolute",
        inset: isActive ? "auto" : 0,
        zIndex: isActive ? 1 : 0,
        pointerEvents: isActive ? "auto" : "none",
      }}
      aria-hidden={!isActive}
    >
      {children}
    </div>
  );
};

interface SlideContentProps {
  children: React.ReactNode;
  index: number;
  side: "left" | "right";
}

const SideBySideCarouselSlideLeftContent = ({ children, index }: Omit<SlideContentProps, "side">) => {
  const { activeIndex } = useCarouselContext();
  const isActive = activeIndex === index;

  return (
    <m.div
      animate={{ opacity: isActive ? 1 : 0 }}
      initial={false}
      transition={{
        duration: 0.3,
        delay: isActive ? 0.15 : 0,
        ease: "easeOut",
      }}
      className='flex h-full w-full flex-col justify-between gap-32'
    >
      {children}
    </m.div>
  );
};

const SideBySideCarouselSlideRightContent = ({
  children,
  index,
  scaleAnimation = true,
}: Omit<SlideContentProps, "side"> & { scaleAnimation?: boolean }) => {
  const { activeIndex } = useCarouselContext();
  const isActive = activeIndex === index;

  return (
    <m.div
      animate={{
        opacity: isActive ? 1 : 0,
        scale: scaleAnimation ? (isActive ? 1 : 0.98) : 1,
        filter: isActive ? "blur(0px)" : "blur(8px)",
      }}
      initial={false}
      transition={{
        delay: isActive ? 0.15 : 0,
        duration: 0.3,
        ease: "easeOut",
      }}
      className='h-full w-full'
    >
      {children}
    </m.div>
  );
};

interface BlockSideBySideDemoCarouselProps {
  ariaLabel?: string;
  children: React.ReactNode;
  slideTitles?: string[];
  carouselId: string;
}

const SideBySideCarousel = ({
  children,
  slideTitles = [],
  carouselId,
  ariaLabel = "Demo carousel",
}: BlockSideBySideDemoCarouselProps) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const tablistRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("ClientKeys.SideBySideCarousel");

  const childArray = Children.toArray(children);
  const slideCount = childArray.length;

  const goToSlide = (index: number) => {
    setActiveSlideIndex(index);
  };

  const goToPrevious = () => {
    const newIndex = activeSlideIndex > 0 ? activeSlideIndex - 1 : slideCount - 1;
    goToSlide(newIndex);
  };

  const goToNext = () => {
    const newIndex = activeSlideIndex < slideCount - 1 ? activeSlideIndex + 1 : 0;
    goToSlide(newIndex);
  };

  // Handle keyboard navigation for tabs
  const handleKeyDown = (event: React.KeyboardEvent, currentIndex: number) => {
    let newIndex = currentIndex;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : slideCount - 1;
        break;
      case "ArrowRight":
        event.preventDefault();
        newIndex = currentIndex < slideCount - 1 ? currentIndex + 1 : 0;
        break;
      case "Home":
        event.preventDefault();
        newIndex = 0;
        break;
      case "End":
        event.preventDefault();
        newIndex = slideCount - 1;
        break;
      default:
        return;
    }

    goToSlide(newIndex);
    // Focus the new tab
    const buttons = tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.[newIndex]?.focus();
  };

  return (
    <CarouselContext.Provider value={{ activeIndex: activeSlideIndex }}>
      <div aria-label={ariaLabel} aria-roledescription='carousel' className='relative w-full' role='group'>
        {/* Slide container - all slides rendered for SEO */}
        <div aria-atomic='false' aria-live='polite' className='relative w-full'>
          {children}
        </div>

        {/* Slide indicators - iOS style floating */}
        {slideCount > 1 && (
          <div className='pointer-events-none absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-1/2'>
            <div className='pointer-events-auto flex items-center gap-8'>
              {/* Previous button */}
              <button
                aria-label={t("previous_slide")}
                className='flex size-32 items-center justify-center rounded-full border border-default bg-eggshell text-default hover:bg-[#FAF9F8]'
                onClick={goToPrevious}
              >
                <ChevronLeft size={16} className='opacity-60' />
              </button>

              {/* Slide indicator dots */}
              <div
                aria-label='Carousel navigation'
                className='flex gap-8 rounded-full border border-default bg-eggshell px-24 py-12'
                ref={tablistRef}
                role='tablist'
              >
                {Array.from({ length: slideCount }).map((_, index) => (
                  <button
                    aria-controls={`${carouselId}-carousel-panel-${index}`}
                    aria-label={slideTitles[index] || t(`slide_number`, { number: index + 1 })}
                    aria-selected={activeSlideIndex === index}
                    className={cn(
                      "h-8 w-8 rounded-full transition-all duration-300",
                      activeSlideIndex === index ? "bg-inverse" : "bg-overlay-darker hover:bg-overlay-darkest-plus",
                    )}
                    id={`${carouselId}-carousel-tab-${index}`}
                    key={index}
                    onClick={() => goToSlide(index)}
                    onKeyDown={e => handleKeyDown(e, index)}
                    role='tab'
                    tabIndex={activeSlideIndex === index ? 0 : -1}
                  />
                ))}
              </div>

              {/* Next button */}
              <button
                aria-label={t("next_slide")}
                className='flex size-32 items-center justify-center rounded-full border border-default bg-eggshell text-default hover:bg-[#FAF9F8]'
                onClick={goToNext}
              >
                <ChevronRight size={16} className='opacity-60' />
              </button>
            </div>
          </div>
        )}
      </div>
    </CarouselContext.Provider>
  );
};

export {
  SideBySideCarousel,
  SideBySideCarouselSlide,
  SideBySideCarouselSlideLeftContent,
  SideBySideCarouselSlideRightContent,
};
